import { createAuthClient } from "better-auth/client";
import { deviceAuthorizationClient } from "better-auth/client/plugins";

import { args } from "../../lib/args/args";
import { deviceTokenError } from "../../lib/token/device-token-error";
import { nextPollDelay } from "../../lib/retry/next-poll-delay";
import { CliError } from "../../lib/cli/cli-error";
import { openBrowser } from "../../lib/browser/open-browser";
import { out } from "../../lib/output/out";
import { MIN_BACKOFF } from "@/commands/login/login-constants";
import { CLIENT_ID } from "@/commands/client/client-id-constants";
import { sleep } from "@/commands/sleep/sleep";
import { completeLogin } from "@/commands/complete/complete-login";
import { resolveLoginBaseUrl } from "@/commands/login/resolve-login-base-url";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";

/**
 * `hepha login` — OAuth 2.0 device authorization (RFC 8628).
 *
 * The CLI never sees a password and never asks for a pasted token: it gets a
 * short code, a human approves it in a browser that is already signed in to
 * Hephaistos, and only then does a session token exist. That approval is also
 * what makes a CLI session count as "a human" for guardrail decisions.
 */
export async function loginCommand(parsed: ParsedArgs): Promise<void> {
  args.expect(parsed, { flags: ["url"] });

  const baseUrl = resolveLoginBaseUrl(parsed);

  const authClient = createAuthClient({
    baseURL: baseUrl,
    plugins: [deviceAuthorizationClient()],
  });

  const { data: device, error: deviceError } = await authClient.device.code({
    client_id: CLIENT_ID,
    scope: "openid profile email",
  });

  if (deviceError || !device) {
    throw new CliError(
      deviceError?.error_description ??
        `Could not start a device login against ${baseUrl}.`,
    );
  }

  const verificationUrl =
    device.verification_uri_complete ?? device.verification_uri;

  out.line();
  out.line(`  Code   ${out.paint(out.cell(device.user_code), "bold")}`);
  out.line(`  Open   ${out.cell(verificationUrl)}`);
  out.line();
  out.line(out.paint("  Waiting for approval…", "dim"));

  openBrowser(verificationUrl);

  let interval = device.interval ?? 5;
  const deadline = Date.now() + (device.expires_in ?? 900) * 1000;

  while (Date.now() < deadline) {
    // Never sleep past the code's own expiry (see nextPollDelay).
    const delay = nextPollDelay(interval, (deadline - Date.now()) / 1000);
    if (delay === null) {
      break;
    }
    await sleep(delay);

    const { data: token, error } = await authClient.device.token({
      grant_type: "urn:ietf:params:oauth:grant-type:device_code",
      device_code: device.device_code,
      client_id: CLIENT_ID,
    });

    // A 429 is transient by definition, and giving up on one abandoned a login
    // the human was about to approve — with "Device authorization failed." as
    // the only clue, because the rate limiter answers a plain message and none
    // of the OAuth error codes below. Back off for as long as the server asks
    // (it answers `x-retry-after`), with a floor, and keep polling.
    if (error?.status === 429) {
      interval = Math.max(
        deviceTokenError.retryAfter(error) ?? interval * 2,
        MIN_BACKOFF,
      );
      continue;
    }

    if (token?.access_token) {
      await completeLogin(baseUrl, token.access_token);
      return;
    }

    if (!error) {
      continue;
    }

    switch (error.error) {
      case "authorization_pending":
        continue;
      case "slow_down":
        interval += 5;
        continue;
      case "access_denied":
        throw new CliError("Access denied — the request was rejected.");
      default:
        // The server's own message, when it has one. Collapsing everything
        // that is not an OAuth code into "Device authorization failed." is how
        // a rate-limit refusal looked like a broken device flow.
        throw new CliError(
          error.error_description ||
            deviceTokenError.message(error) ||
            `Device authorization failed (HTTP ${error.status}).`,
        );
    }
  }

  throw new CliError("The code expired before it was approved. Try again.");
}
